import React, { useLayoutEffect, useRef } from "react";

import {
  AccountDetailsSetupMessage,
  useNavigateToAccountDetails,
} from "./account-details-setup-message";

const SETUP_CANVAS_PAD_TOP = 28;
const SETUP_CANVAS_PAD_BOTTOM = 48;
const SETUP_MIN_HEIGHT_FALLBACK = 420;

function resolveSetupTitle({ hasVendors, hasCustomers }) {
  if (!hasVendors && !hasCustomers) return "Set up the relationship matrix";
  if (!hasVendors) return "Add your exec team to continue";
  return "Add account contacts to continue";
}

function MatrixSetupOverlayCard({ accountId, hasVendors, hasCustomers }) {
  const navigateToAccountDetails = useNavigateToAccountDetails();

  return (
    <div className="sc-card">
      <div className="sc-card-title">
        {resolveSetupTitle({ hasVendors, hasCustomers })}
      </div>
      <AccountDetailsSetupMessage
        accountId={accountId}
        className="sc-step-body"
      />
      <button
        type="button"
        className="btn btn-pri"
        style={{ width: "100%", marginTop: 4 }}
        onClick={() => navigateToAccountDetails(accountId)}
      >
        Go to Account Details →
      </button>
    </div>
  );
}

export function MatrixSetupShell({
  show,
  accountId,
  hasVendors,
  hasCustomers,
  children,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;

    if (!show) {
      wrap.style.minHeight = "";
      if (canvasRef.current) canvasRef.current.style.top = "";
      return undefined;
    }

    const syncLayout = () => {
      const legend = wrap.querySelector(".matrix-legend");
      const thead = wrap.querySelector(".matrix-tbl thead");
      const card = canvasRef.current?.querySelector(".sc-card");
      const legendH = legend?.offsetHeight ?? 0;
      const theadH = thead?.offsetHeight ?? 120;
      const cardH = card?.offsetHeight ?? 220;
      const offsetTop = legendH + theadH;

      wrap.style.minHeight = `${Math.max(
        SETUP_MIN_HEIGHT_FALLBACK,
        offsetTop + SETUP_CANVAS_PAD_TOP + cardH + SETUP_CANVAS_PAD_BOTTOM
      )}px`;

      if (canvasRef.current) {
        canvasRef.current.style.top = `${offsetTop}px`;
      }
    };

    syncLayout();

    const ro = new ResizeObserver(syncLayout);
    ro.observe(wrap);
    if (canvasRef.current) ro.observe(canvasRef.current);

    return () => ro.disconnect();
  }, [show, hasVendors, hasCustomers, accountId]);

  return (
    <div
      ref={wrapRef}
      className={`matrix-wrap matrix-wrap--fill${show ? " matrix-wrap--setup" : ""}`}
      style={{ marginTop: 4, width: "100%" }}
    >
      {show ? (
        <div ref={canvasRef} className="matrix-setup-canvas" aria-live="polite">
          <MatrixSetupOverlayCard
            accountId={accountId}
            hasVendors={hasVendors}
            hasCustomers={hasCustomers}
          />
        </div>
      ) : null}
      <div className={show ? "matrix-ghost-mode" : undefined}>{children}</div>
    </div>
  );
}

export default MatrixSetupShell;
